import imagePlaceholder from '@/assets/placeholder.png';
import Modal from '@/components/Modal';
import Constants from '@/Constants';
import List from '@/models/List';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { archiveListRequest, deleteListRequest, publishListRequest } from '@/store/list/action';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import './style.scss';

interface CourseCardProps {
    item: List;
}

const CourseCard = ({ item }: CourseCardProps) => {
    const dispatch = useDispatch();

    const [icon, setIcon] = useState('');
    const [alertText, setAlertText] = useState('');
    const [action, setAction] = useState('');
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);

    const { id, status, type } = item;

    const handleModalChange = (icon: string, text: string, action: string) => {
        setIcon(icon);
        setAlertText(text);
        setAction(action);
    };

    const renderActions = () => {
        switch (status) {
            case 'PUBLISHED':
                return (
                    <>
                        <Link className="dropdown-content" to={`/contents/lms/courses/${id}`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                    </>
                );
            case 'DRAFT':
                return (
                    <>
                        <Link className="dropdown-content" to={`/contents/lms/courses/${id}`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('delete', strings.DELETE_COURSE_CONFIRMATION, 'delete')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">delete</i>
                            <span>{strings.REMOVE}</span>
                        </div>
                    </>
                );
            case 'ARCHIVED':
                return (
                    <div
                        className="dropdown-content"
                        data-target={`#alert-modal-${id}`}
                        data-toggle="modal"
                        onClick={() => handleModalChange('undo', strings.UNARCHIVE_LIST_CONFIRMATION, 'publish')}
                        onKeyDown={() => {}}
                        role="button"
                        tabIndex={0}
                    >
                        <i className="material-icons-outlined va-t m-right--xxs">undo</i>
                        <span>{strings.UNARCHIVE}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleAction = () => {
        switch (action) {
            case 'archive':
                if (item.id) return dispatch(archiveListRequest(item.id));
                break;
            case 'publish':
                if (item.id) return dispatch(publishListRequest(item.id));
                break;
            case 'delete':
                if (item.id) return dispatch(deleteListRequest(item.id));
                break;
            default:
                return null;
        }
        return null;
    };

    const formatCreatedOn = (value?: Date) => {
        const locales = strings._LANGUAGE === 'pt' ? 'pt-BR' : 'en-US';
        if (!value) return value;
        const data = new Date(value);
        return data.toLocaleDateString(locales);
    };

    const formatItemString = () => {
        if (item.lessonAmount) {
            return item.lessonAmount > 1 ? strings.ITEMS : strings.ITEM;
        }
        return strings.ITEM;
    };

    return (
        <>
            <Modal
                content={alertText}
                handleLeftClick={handleAction}
                icon={icon}
                leftButtonText={strings.YES}
                rightButtonText={strings.NO}
                target={`alert-modal-${id}`}
            />
            <div className="item-card-lms-list">
                <div className="content-top">
                    <img alt="" src={item.mainImage || imagePlaceholder} />
                    {(item.organizationId !== null || identity.user.admin) && (
                        <div className="dropdown">
                            <i className="material-icons-outlined align-middle more" data-toggle="dropdown">
                                {Constants.icons.MORE_HORIZ}
                            </i>
                            <div className="dropdown-menu dropdown-menu-right">
                                <div className="triangle-container">
                                    <div className="dropdown-triangle" />
                                    <div className="dropdown-triangle-border" />
                                </div>
                                {renderActions()}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-3 d-flex flex-row w-100">
                    <span className="item-type">{type == 'WORKSHOP' ? strings.WORKSHOP : strings.COURSE}</span>
                    <span className="ml-auto rate">
                        <i className="material-icons">{Constants.icons.STAR}</i>
                        {item.rating || 0.0}
                    </span>
                </div>

                <div className="item-card-content">
                    <span className="item-title" title={item.name}>
                        {item.name}
                    </span>
                    <span className="item-info-key">
                        {strings.AUTHOR}:{' '}
                        <span className="item-info-value" title={item.author}>
                            {item.author}
                        </span>
                    </span>
                    <span className="item-info-key">
                        {strings.LANGUAGE}: <span className="item-info-value">{courseService.getLanguageRendering(item.locale, strings)}</span>
                    </span>
                    <span className="item-info-key">
                        {strings.CREATED_AT}: <span className="item-info-value">{formatCreatedOn(item.createdOn) || '--/--'}</span>
                    </span>
                    <span className="item-info-key">
                        {strings.DURATION}: <span className="item-info-value">{item.workload && `${item.workload} min`}</span>
                    </span>
                    <span className="item-info-key">
                        {strings.CLASSES}: <span className="item-info-value">{item.classAmount || 0}</span>
                    </span>
                </div>

                <div className="item-actions">
                    <span className="item-count">
                        {item.lessonAmount || 0} <span>{formatItemString()}</span>
                    </span>
                    <div className="d-flex">
                        {item.status === 'PUBLISHED' && (identity.user.admin || identity.person.dynamicAttributes?.selectedProfile === 'HR') && (
                            <>
                                <Link className="btn btn-light btn-sm" to={`/contents/lms/courses/${id}/classes`}>
                                    {strings.MANAGE_CLASSES}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CourseCard;
